<template>
	<app-header-teleport>
		<div class="flex flex-1 items-center gap-3">
			<app-title title="Create order" class="flex" />
			<restaurant-autocomplete
				v-if="!isManagingOneBusiness"
				:model-value="order['restaurant']"
				@update:model-value="updateSelectedBusiness"
			/>
		</div>
	</app-header-teleport>
	<app-section v-if="transportCostError" class="flex flex-1 items-center border-primary border-2">
		<app-icon class="text-primary text-2xl mr-2" icon="info" />
		<span>{{ transportCostError }}</span>
	</app-section>
	<div class="flex flex-col md:flex-row mt-4 gap-3">
		<div class="w-full md:w-1/3">
			<app-section class="flex flex-1 flex-col">
				<app-title class="small mb-2" title="Customer" />
				<div class="flex">
					<app-select
						class="w-24 mr-2"
						placeholder="Prefix"
						:can-deselect="false"
						:options="prefixes"
						:searchable="true"
						v-model="order.customer.phonePrefix"
						@select="checkIfCustomerExists(order.customer)"
					/>
					<div class="flex w-full relative">
						<app-input
							:loading="loadingExist"
							:class="`${customerExists ? 'border-success' : 'border-primary'} flex-1 w-full`"
							name="Phone"
							:placeholder="`${customerExists ? 'Baboon Customer' : 'Phone'}`"
							v-model="order.customer.phone"
							@update:modelValue="checkIfCustomerExists(order.customer)"
							:debounceInterval="1000"
						/>
					</div>
				</div>
				<div class="flex items-center">
					<app-title title="Address" class="small flex flex-1" />
					<app-button
						class="btn btn-primary transparent small"
						:icon="isCreatingAddress ? 'th-list' : 'plus'"
						@click="toggleAddressCreation"
						:disabled="!order['customer']"
						:loading="creatingAddress"
					/>
				</div>
				<app-line />
				<div v-if="!order['customer'].phone">Please select a customer first</div>
				<div v-else>
					<transition name="slide-right" mode="out-in">
						<div v-if="!isCreatingAddress && order['customer'].id > 0">
							<div v-if="addresses.length > 0">
								<p class="title">Street</p>
								<app-select
									class="m-0"
									v-model="order['customerAddress']"
									:options="addresses"
									:loading="loadingAddresses"
									:object="true"
									@select="loadingAddresses = false"
								/>
								<div class="mb-3">
									<p class="title">Address Details</p>
									<div class="flex justify-between items-center">
										<app-input
											class="w-full mr-1"
											v-if="editAddressDetails"
											name="Details"
											v-model="order['customerAddress'].addressDetails"
										/>
										<p v-else class="ml-2">{{ order['customerAddress'].addressDetails }}</p>
										<app-button
											@click="editAddress"
											:icon="editAddressDetails ? 'save' : 'pencil'"
											:class="`btn btn-info transparent small force-small text-13`"
										/>
									</div>
								</div>
								<transition name="slide-right" mode="out-in">
									<div class="h-64" v-if="!!order['customerAddress']">
										<app-map-image
											class="h-64"
											:coordinates="[
												order['customerAddress'].latitude,
												order['customerAddress'].longitude,
											]"
										/>
									</div>
								</transition>
							</div>
						</div>
						<div v-else>
							<app-geo-search-select
								v-if="renderFilters"
								name="Street"
								label="Street"
								:placeholder="newAddress.addressStreet || 'Write Customer address here...'"
								@placechanged="getAddressData"
							/>
							<app-input name="Details" placeholder="Details" v-model="newAddress.addressDetails" />
							<transition name="slide-right" mode="out-in">
								<div class="h-64" v-if="!!newAddress['latitude']">
									<app-map-picker
										class="h-64"
										@coordinates-chosen="onCoordinatesChosen"
										:coordinates="[newAddress.latitude, newAddress.longitude]"
									/>
								</div>
							</transition>
						</div>
					</transition>
				</div>
			</app-section>
		</div>
		<div v-if="order['customer'].id" class="w-full flex flex-col md:w-1/3">
			<app-section class="flex flex-1 flex-col" v-if="!order['customer'].id">
				<app-title class="small" title="Menu" />
				<app-line />
				<div>Please select a customer first than menu.</div>
			</app-section>
			<menu-section
				v-else
				:order="order"
				:business-id="order.restaurant"
				:restaurantCurrency="order.currency"
				:customer-id="order.customer.id"
				@addToBasket="addToBasket"
				@addCustomItem="showCustomItemModal"
			/>
		</div>
		<div v-if="order['customer'].id" class="w-full flex flex-col md:w-1/3">
			<app-section v-if="canChangeDeliveryType || isBusinessCashless">
				<div v-if="canChangeDeliveryType" class="flex flex-row gap-5">
					<p class="title">Delivery / Takeaway:</p>
					<app-switch
						class="w-24 font-bold"
						onLabel="Delivery"
						offLabel="Takeaway"
						:model-value="deliveryType"
						@change="toggleTakeaway"
					/>
				</div>
				<app-line v-if="canChangeDeliveryType && isBusinessCashless" />
				<div v-if="isBusinessCashless" class="flex flex-1 gap-5">
					<p class="title">{{ $t('pays') }}:</p>
					<app-radio-group
						:disabled="deliveryType === 0"
						:options="[
							{ value: 0, label: 'Restaurant' },
							{ value: 1, label: 'Customer' },
						]"
						v-model="order.isManualOrderCashless"
					/>
				</div>
			</app-section>
			<basket-section
				ref="basketSection"
				:items="allItems"
				:subtotal="subtotal"
				:restaurantCurrency="order.currency"
				:transport-cost="order.transportCost"
				:serviceFee="order.serviceFee"
				:restaurant="Number(order.restaurant)"
				:discount-value="order.discountValue || 0"
				@removeFromBasket="removeFromBasket"
			/>
			<app-section class="flex flex-row">
				<app-text-area
					:disabled="!allItems.length"
					class="flex flex-1"
					:rows="descriptionRows"
					name="Description"
					placeholder="Order Details"
					v-model="order.description"
				/>
			</app-section>
			<div class="mt-3">
				<app-button
					class="btn btn-success w-full"
					@click="createOrder"
					:loading="creating"
					:disabled="disableCreate"
				>
					<div class="flex gap-5 items-center justify-between">
						<app-icon icon="save" />
						<span>Create Order</span>
						<span v-if="total">{{ $filters.formatCurrency(total, order.currency) }}</span>
					</div>
				</app-button>
			</div>
		</div>
	</div>
	<app-modal v-if="isCustomItemModalVisible" @close="hideCustomItemModal" headerClass="px-4 py-0">
		<template v-slot:header>
			<div class="py-2">
				<app-title class="small" title="Special Item" />
			</div>
		</template>
		<div style="width: 400px; max-width: 400px;">
			<app-input name="customItemName" placeholder="Name" v-model="customItem.itemName" />
			<app-input name="customItemPrice" type="number" placeholder="Price" v-model="customItem.itemPrice" />
			<app-button class="btn btn-success w-full" icon="plus" @click="addCustomItem">Add</app-button>
		</div>
	</app-modal>
</template>

<script>
	import AppTitle from '@/components/app/AppTitle';
	import RestaurantAutocomplete from '@/components/sales/Autocomplete';
	import CustomerService from '@/services/CustomerService';
	import AppMapImage from '@/components/app/AppMapImage';
	import AppMapPicker from '@/components/app/AppMapPicker';
	import RestaurantService from '@/services/sales/BusinessService';
	import OrderService from '@/services/OrderService';
	import UtilityService, { prepAutocompleteData } from '@/services/UtilityService';
	import AppIcon from '@/components/app/AppIcon';
	import AppGeoSearchSelect from '@/components/app/AppGeoSearchSelect';
	import MenuSection from '@/components/orders/details/MenuSection';
	import { isEqual, omit } from 'lodash';
	import AppModal from '@/components/app/AppModal';
	import BasketSection from '@/components/orders/details/BasketSection';
	import { mapGetters } from 'vuex';
	import AppSwitch from '@/components/app/AppSwitch';
	import AppSelect from '@/components/app/AppSelect.vue';
	import AppInput from '@/components/app/AppInput.vue';
	import B2BOrdersService from '@/services/b2b/B2BOrdersService';
	import UserService from '@/services/UserService';
	import AppTextArea from '@/components/app/AppTextArea.vue';
	import AppRadioGroup from '../../components/app/AppRadioGroup.vue';
	import AppLine from '../../components/app/AppLine.vue';

	const DEFAULT_ORDER = {
		discount: {
			id: 0,
		},
		manually: 1,
		transportCost: 0,
		serviceFee: 0,
		items: [],
		customItems: [],
		currency: {},
		customer: {
			phonePrefix: '+355',
			phone: '',
		},
		description: '',
		isManualOrderCashless: null,
	};

	const DEFAULT_PHONE_PREFIX = '+355';

	const getDefaultPhonePrefix = (user) => (user?.city?.id === 2 ? '+383' : DEFAULT_PHONE_PREFIX);

	const createDefaultOrder = (user = null) => ({
		...DEFAULT_ORDER,
		customer: {
			...DEFAULT_ORDER.customer,
			phonePrefix: getDefaultPhonePrefix(user),
			phone: '',
		},
	});

	export default {
		name: 'CreateOrder',
		components: {
			AppLine,
			AppRadioGroup,
			AppTextArea,
			AppInput,
			AppSelect,
			BasketSection,
			AppModal,
			MenuSection,
			AppTitle,
			RestaurantAutocomplete,
			AppMapImage,
			AppMapPicker,
			AppIcon,
			AppGeoSearchSelect,
			AppSwitch,
		},
		data: () => ({
			transportCostError: null,
			isProductsModalVisible: false,
			isCustomItemModalVisible: false,
			isCreatingAddress: false,
			loadingAddresses: false,
			creating: false,
			creatingAddress: false,
			addresses: [],
			customItem: {},
			newAddress: {
				id: -1,
				addressStreet: '',
				addressDetails: '',
				primary: false,
				active: true,
			},
			order: createDefaultOrder(),
			takeAwayPoint: {},
			deliveryType: 1,
			canChangeDeliveryType: true,
			renderFilters: true,
			prefixes: [],
			customerExists: 0,
			loadingExist: false,
			editAddressDetails: false,
			isBusinessCashless: null,
		}),
		computed: {
			...mapGetters({
				user: 'user',
			}),
			isManagingOneBusiness() {
				return this.user?.restaurants?.length === 1;
			},
			businessId() {
				return this.isManagingOneBusiness
					? this.user.restaurants[0].restaurantID
					: this.$route.params.businessId;
			},
			allItems() {
				return [...this.order.customItems, ...this.order.items];
			},
			subtotal() {
				// todo for allItems
				const items = this.order.items
					.filter((p) => p.quantity > 0)
					.map((p) => {
						const itemPrice = p.variantItem?.id ? p.variantItem.price : p.itemPrice;
						const toppingsPrice = p.toppingItems?.length
							? p.toppingItems
									.map((topItem) => topItem.quantity * topItem.price)
									.reduce((a, b) => {
										return a + b;
									}, 0)
							: 0;
						return p.quantity * (itemPrice + toppingsPrice);
					});
				return items.reduce((a, b) => {
					return a + b;
				}, 0);
			},
			total() {
				const subtotal = this.subtotal;
				const transport = this.order.transportCost ? this.order.transportCost : 0;
				return subtotal + transport + (this.$refs.basketSection?.localServiceFee || 0);
			},
			disableCreate() {
				return (
					this.creating ||
					this.transportCostError ||
					!this.allItems.length ||
					this.$refs.basketSection?.serviceFeeLoading
				);
			},
			descriptionRows() {
				return this.order.description?.length / 90 + this.order.description.split('\n').length;
			},
		},
		methods: {
			editAddress() {
				this.editAddressDetails = !this.editAddressDetails;
				if (!this.editAddressDetails) {
					this.updateAddress();
				}
			},
			updateAddress() {
				CustomerService.updateAddress(this.order.customer.id, this.order['customerAddress'])
					.then(() => {
						this.$notificationService.success('Address updated successfully');
					})
					.catch((e) => {
						this.$notificationService.error(e);
					});
			},
			removeFromBasket(productItem) {
				const productIndex = this.order.items.findIndex((item) => item.id === productItem.id);
				if (productIndex === -1) {
					this.order.customItems.splice(
						this.order.customItems.findIndex((item) => item.id === productItem.id),
						1
					);
				} else {
					this.order.items[productIndex].quantity -= 1;
					if (this.order.items[productIndex].quantity === 0) {
						this.order.items.splice(productIndex, 1);
					}
				}
			},
			addToBasket(productItem) {
				const productIndex = this.order.items.findIndex((item) => item.id === productItem.id);
				if (productIndex === -1) {
					this.order.items.push(productItem);
				} else if (productItem.isComplex) {
					const complexIndex = this.order.items.findIndex(
						(item) =>
							item.id === productItem.id && isEqual(omit(item, 'quantity'), omit(productItem, 'quantity'))
					);
					if (complexIndex === -1) {
						this.order.items.push(productItem);
					} else {
						this.order.items[complexIndex].quantity += productItem.quantity;
					}
				} else {
					this.order.items[productIndex].quantity += productItem.quantity;
				}
			},
			getAddresses({ id }) {
				if (id !== -1) {
					this.loadingAddresses = true;
					CustomerService.getAddresses(id)
						.then(
							(addresses) => {
								const parsedAddresses = prepAutocompleteData(
									addresses.filter((a) => a.active),
									'addressStreet',
									'id'
								);
								this.addresses = parsedAddresses;
								this.order.customerAddress = parsedAddresses.find((address) => address.primary) || {};
							},
							(error) => {
								this.$notificationService.error(error);
							}
						)
						.finally(() => {
							this.loadingAddresses = false;
							this.isCreatingAddress = false;
						});
				}
			},
			showCustomItemModal() {
				this.isCustomItemModalVisible = true;
			},
			hideCustomItemModal() {
				this.isCustomItemModalVisible = false;
			},
			toggleAddressCreation() {
				if (this.order['customer'].id > 0) {
					this.isCreatingAddress = !this.isCreatingAddress;
				}
			},
			getTransportCost() {
				try {
					const id = this.order.restaurant;
					const latitude =
						this.isCreatingAddress || this.order.customer.id === -1
							? this.newAddress['latitude']
							: this.order.customerAddress['latitude'];
					const longitude =
						this.isCreatingAddress || this.order.customer.id === -1
							? this.newAddress['longitude']
							: this.order.customerAddress['longitude'];
					if (id && latitude && longitude) {
						if (this.deliveryType === 0) {
							this.order.transportCost = 0;
						} else {
							RestaurantService.getTransportCost(id, {
								latitude,
								longitude,
								customerId: this.order.customer.id,
							}).then(
								(cost) => {
									this.transportCostError = null;
									this.order.transportCost = cost['transportcost'];
									if (this.order.items.length) {
										this.$refs.basketSection.getServiceFee();
									}
								},
								(error) => {
									this.transportCostError = UtilityService.extractErrorMessage(error);
									this.$notificationService.error(error);
								}
							);
						}
					}
				} catch (e) {
					// console.info('Just something not set yet');
				}
			},
			restaurantCustomItem() {
				try {
					RestaurantService.getCustomItem(this.order.restaurant)
						.then((customItem) => {
							this.customItem = customItem;
						})
						.catch(() => {
							this.customItem = {};
						});
				} catch (e) {
					this.customItem = {};
				}
			},
			async createOrder() {
				if (!this.order['customerAddress'] || !this.order['customerAddress'].id || this.isCreatingAddress) {
					try {
						let address = { ...this.newAddress };
						if (this.order.customer && this.order.customer.id !== -1) {
							this.creatingAddress = true;
							address = await CustomerService.createAddress(this.order.customer.id, this.newAddress);
							this.creatingAddress = false;
							this.getAddresses(this.order.customer);
						}
						this.order['customerAddress'] = address;
						this.getTransportCost();
					} catch (e) {
						this.creatingAddress = false;
						return;
					}
				}
				this.creating = true;
				const order = {
					...this.order,
					total: this.total,
					takeAwayPoint: this.takeAwayPoint,
					serviceFee: this.$refs.basketSection.localServiceFee,
					notRegisteredUser: 1,
					// Careful add keys in prepareOrderData!
				};
				OrderService.createComplex(order)
					.then(() => {
						location.reload();
						this.$notificationService.success('Order created successfully');
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.creating = false;
					});
			},
			//Todo add custom item
			addCustomItem() {
				this.customItem.quantity = 1;
				this.order.customItems.push({ ...this.customItem, id: this.customItem.itemName });
				this.hideCustomItemModal();
			},
			removeCustomItemProduct(index) {
				if (index > this.order.customItems.length - 1) {
					this.order.items.splice(index - this.order.customItems.length, 1);
				} else {
					this.order.customItems.splice(index, 1);
				}
			},
			getAddressData(address) {
				this.newAddress.latitude = address.latitude;
				this.newAddress.longitude = address.longitude;
				this.newAddress.addressStreet = address.route + ' ' + address.locality;
			},
			onCoordinatesChosen({ lat, lng }) {
				this.newAddress.latitude = lat;
				this.newAddress.longitude = lng;
				CustomerService.getAddress(lat, lng).then(
					(street) => {
						if (street) {
							this.newAddress.addressStreet = street.address.road;
						}
						this.getTransportCost();
					},
					() => {
						this.getTransportCost();
					}
				);
			},
			updateSelectedBusiness(val) {
				this.order = createDefaultOrder(this.user);
				this.order.restaurant = val;
				this.$router.replace({ params: { businessId: val } });
				this.order.customer.phone = '';
				this.checkIfCustomerExists(this.order.customer);
			},
			applyDefaultPhonePrefix() {
				if (!this.order.customer.phonePrefix || this.order.customer.phonePrefix === DEFAULT_PHONE_PREFIX) {
					this.order.customer.phonePrefix = getDefaultPhonePrefix(this.user);
				}
			},
			toggleTakeaway(event) {
				this.deliveryType = event;
				if (this.deliveryType === 0) {
					this.order.transportCost = 0;
					this.$refs.basketSection.localServiceFee = 0;
					this.order.isManualOrderCashless = 0;
					RestaurantService.takeAwayPoints(this.order.restaurant)
						.then((points) => {
							this.takeAwayPoint = points[0];
							this.$nextTick(() => {
								this.order.transportCost = 0;
								this.$refs.basketSection.localServiceFee = 0;
							});
						})
						.catch((error) => {
							this.$notificationService.error(error);
							this.takeAwayPoint = {};
						});
				} else {
					this.takeAwayPoint = {};
					this.getTransportCost();
					this.order.isManualOrderCashless = this.isBusinessCashless;
				}
			},
			checkIfCanChangeDeliveryType() {
				const restaurant = this.user?.restaurants?.find(
					(r) => r.restaurantID === Number(this.order.restaurant)
				);
				this.getCurrency(restaurant);
				this.canChangeDeliveryType = restaurant.deliveryModule === restaurant.takeAwayModule;
				this.isBusinessCashless = restaurant.businessExtraData.cashless;
				this.order.isManualOrderCashless = restaurant.businessExtraData.cashless;
			},
			getCurrency(restaurant) {
				this.order.currency = restaurant.currency;
			},
			reRenderFilters() {
				this.renderFilters = false;
				this.$nextTick(() => {
					this.renderFilters = true;
				});
			},
			checkIfCustomerExists(customer) {
				if (customer.phonePrefix && customer.phone.toString().length > 7) {
					this.loadingExist = true;
					B2BOrdersService.checkIfCustomerExists({
						phone: customer.phone,
						prefix: customer.phonePrefix,
					})
						.then((res) => {
							this.customerExists = res.exists;
							if (res.exists) {
								this.order.customer = res.customer;
								this.getAddresses(res.customer);
							} else {
								this.isCreatingAddress = true;
								this.order.customer.id = -1;
							}
						})
						.catch((err) => {
							this.$notificationService.error(err);
						})
						.finally(() => (this.loadingExist = false));
				} else {
					this.customerExists = 0;
				}
			},
			getPrefixes() {
				UserService.getPhonePrefixes().then((res) => {
					this.prefixes = res;
				});
			},
		},
		mounted() {
			this.order.restaurant = this.businessId;
			this.applyDefaultPhonePrefix();
			this.checkIfCanChangeDeliveryType();
			this.getPrefixes();
		},
		watch: {
			'user.city.id': {
				immediate: true,
				handler() {
					this.applyDefaultPhonePrefix();
				},
			},
			'order.restaurant'() {
				this.getTransportCost();
				this.restaurantCustomItem();
				this.deliveryType = 1;
				this.takeAwayPoint = {};
				this.checkIfCanChangeDeliveryType();
			},
			'order.customerAddress'() {
				this.getTransportCost();
			},
			'newAddress.latitude'() {
				this.getTransportCost();
			},
			'newAddress.longitude'() {
				this.getTransportCost();
			},
			'newAddress.addressStreet'() {
				this.reRenderFilters();
			},
			businessId(val) {
				if (parseInt(val)) this.order.restaurant = parseInt(val);
			},
		},
	};
</script>
